import PlusIcon from '@components/icons/PlusIcon';
import { useMediaQuery } from '@material-ui/core';
import CheckIcon from '@mui/icons-material/Check';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import VideoInstruction from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/VideoInstruction';
import CargoSpecialConditional from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/сargoSpecialConditional';
import {
  RouteWidgetBaseParameters,
  TruckParameters,
  TruckRouteCalculationParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import useDelivery from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CargoSelection } from '../CargoSelection';
import TruckParametersItemComponent from './TruckParametersItemComponent';

export interface TruckParametersProps {
  BaseData: RouteWidgetBaseParameters;
  truckParameters: TruckRouteCalculationParameters;
  mainLanguage: string; // Текущий основной язык
  systemLanguage: string; // Системный язык
  baseConditionsSlot?: React.ReactNode;
  resetSlot?: React.ReactNode;
  copyNotificationVisible: boolean;

  handleDeleteTruckParameters(index: number): void;
  onChangeCargoFieldsFilled: { (values: boolean[]): void };
}

const TruckParametersComponent: FC<TruckParametersProps> = (props) => {
  const {
    baseConditionsSlot,
    resetSlot,
    handleDeleteTruckParameters,
    onChangeCargoFieldsFilled,
    truckParameters: { truckParameters: cargoParameters, isDangerousCargo },
    copyNotificationVisible,
  } = props;

  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const setRouteParameter = useRouteParams().setState;
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const theme = useTheme();
  const isNotMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const { t } = useTranslation('Actions');

  const handleAddNewTruck = () => {
    const { id = '' } = props.BaseData.trucks[0];

    setRouteParameter((current) => ({
      ...current,
      truckParameters: {
        ...current.truckParameters,
        truckParameters: [
          ...current.truckParameters.truckParameters,
          {
            truckId: id,
            truckQuantity: 1,
            eachTruckWeight: 1500,
            withPallets: false,
            palletsLoaded: [],
          },
        ],
      },
    }));
  };

  /**
   * Обработка изменения параметров фуры
   * @param truckParameters
   * @param index
   * @param field
   */
  const handleChangeTruckParameters = (
    truckParameters: TruckParameters,
    field: keyof TruckParameters,
    index: number
  ) => {
    setRouteParameter((current) => {
      // Создание копии массива truckParameters
      const updatedTrucks = [...current.truckParameters.truckParameters];

      if (field === 'truckId') {
        truckParameters.eachTruckWeight = 1500;
        truckParameters.withPallets = false;
        truckParameters.palletsLoaded = [];
      }

      if (field === 'withPallets' && truckParameters.withPallets === false) {
        truckParameters.palletsLoaded = [];
      }

      // Обновление элемента в скопированном массиве
      updatedTrucks[index] = {
        ...truckParameters,
      };

      // Возвращение обновленного состояния
      return {
        ...current,
        truckParameters: {
          ...current.truckParameters,
          truckParameters: updatedTrucks,
        },
      };
    });
  };

  return (
    <>
      <CargoSelection
        isActionsDisabled={isRoutesLoading || isLoading}
        BaseData={props.BaseData}
        mainLanguage={props.systemLanguage}
        cargoSelectionType="ftl"
      />
      <section className="cargo-list">
        {cargoParameters.map((truck, i) => (
          <div key={`list-item--${i}`} className={'cargo-item'}>
            <TruckParametersItemComponent
              Position={1 + i}
              BaseData={props.BaseData}
              mainLanguage={props.mainLanguage}
              systemLanguage={props.systemLanguage}
              TruckParameters={truck}
              onChange={(params, field) => handleChangeTruckParameters(params, field, i)}
              isDeletable={cargoParameters.length > 1}
              onDelete={() => handleDeleteTruckParameters(i)}
              onChangeCargoFieldsFilled={onChangeCargoFieldsFilled}
            />
          </div>
        ))}
        <IconButton
          title={`+ ${t('Actions:Add')}`}
          className={'add-new-cargo'}
          disabled={isRoutesLoading || isLoading}
          onClick={() => handleAddNewTruck()}
        >
          <PlusIcon />
        </IconButton>
      </section>
      <div className="footer">
        <div className={'base-params'}>{baseConditionsSlot}</div>
        <CargoSpecialConditional isDangerousCargo={isDangerousCargo} />
        <div className={'right'}>
          {isNotMobile && <VideoInstruction />}
          {resetSlot && (
            <div className={'routes-calculation-widget__footer-reset-container'}>
              <>
                {copyNotificationVisible && (
                  <Alert
                    icon={<CheckIcon fontSize="inherit" />}
                    severity="success"
                    className="copy-success-alert"
                  >
                    {t('RoutesCalculationWidget:CopyCalculationLinkSuccess')}
                  </Alert>
                )}
                {resetSlot}
              </>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TruckParametersComponent;
